import consoleProxy from "@/utils/console";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

declare global {
  interface Window {
    __FOCUS_DEBUG_FEED_FREEZE?: boolean;
    __FOCUS_DEBUG_FEED_FREEZE_READY?: boolean;
  }
}

const FEED_FREEZE_DEBUG_QUERY_KEY = "debugFeedFreeze";
const ENABLED_VALUES = new Set(["1", "true", "yes", "on"]);
const DISABLED_VALUES = new Set(["0", "false", "no", "off"]);
const logThrottleTimestamps = new Map<string, number>();

const getRoundedTime = (time: number) => Math.round(time * 10) / 10;

const readQueryValue = () => {
  try {
    return new URLSearchParams(window.location.search).get(
      FEED_FREEZE_DEBUG_QUERY_KEY,
    );
  } catch {
    return null;
  }
};

const syncFeedFreezeDebugFromLocation = () => {
  if (
    typeof window === "undefined" ||
    window.__FOCUS_DEBUG_FEED_FREEZE_READY ||
    process.env.NODE_ENV === "production"
  ) {
    return;
  }

  window.__FOCUS_DEBUG_FEED_FREEZE_READY = true;

  const queryValue = readQueryValue()?.toLowerCase();

  if (!queryValue) {
    return;
  }

  try {
    if (ENABLED_VALUES.has(queryValue)) {
      window.localStorage.setItem(LocalStorageKeys.feedFreezeDebug, "1");
    } else if (DISABLED_VALUES.has(queryValue)) {
      window.localStorage.removeItem(LocalStorageKeys.feedFreezeDebug);
    }
  } catch {
    // Ignore storage access issues in restrictive browser contexts.
  }
};

export const isFeedFreezeDebugEnabled = () => {
  if (typeof window === "undefined" || process.env.NODE_ENV === "production") {
    return false;
  }

  syncFeedFreezeDebugFromLocation();

  if (window.__FOCUS_DEBUG_FEED_FREEZE === true) {
    return true;
  }

  try {
    return (
      window.localStorage.getItem(LocalStorageKeys.feedFreezeDebug) === "1"
    );
  } catch {
    return false;
  }
};

type FeedFreezeDebugLevel = "log" | "warn" | "error";

type FeedFreezeDebugOptions = {
  level?: FeedFreezeDebugLevel;
  throttleKey?: string;
  throttleMs?: number;
};

const getLogger = (level: FeedFreezeDebugLevel) => {
  switch (level) {
    case "warn":
      return consoleProxy.warn;
    case "error":
      return consoleProxy.error;
    default:
      return consoleProxy.log;
  }
};

export const feedFreezeDebugLog = (
  scope: string,
  event: string,
  details: Record<string, unknown> = {},
  { level = "log", throttleKey, throttleMs = 0 }: FeedFreezeDebugOptions = {},
) => {
  if (!isFeedFreezeDebugEnabled()) {
    return;
  }

  const now = performance.now();

  if (throttleKey && throttleMs > 0) {
    const lastLogAt = logThrottleTimestamps.get(throttleKey) ?? -Infinity;

    if (now - lastLogAt < throttleMs) {
      return;
    }

    logThrottleTimestamps.set(throttleKey, now);
  }

  getLogger(level)(`[feed-freeze-debug][${scope}] ${event}`, {
    t: getRoundedTime(now),
    ...details,
  });
};

export const getFeedFreezeDebugDuration = (startedAt: number) => {
  return getRoundedTime(performance.now() - startedAt);
};
